import { useState, useEffect } from 'react'
import { ChevronLeft, User, Edit, Search, AlertTriangle } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { MainLayout } from '@/components/layout'
import Button from '../../components/common/Button'
import MapView from '../../components/map/MapView'
import { storageService } from '../../services'
import { API_BASE_URL, API_ENDPOINTS, CATTLE_TYPES } from '../../constants'
import { toast } from 'react-toastify'
import { useGetHeatAlertsQuery } from '@/store'
import { calculateAge } from '@/utils'

interface Farm {
  id: string
  name: string
  farmCode: string
  address?: string
  subdistrict?: string
  district?: string
  province?: string
  postalCode?: string
  landArea?: number
  latitude?: number
  longitude?: number
  status: string
  images?: string[]
  createdAt: string
  updatedAt: string
  userId: string
  user?: {
    id: string
    email: string
    phone?: string
  }
}

interface Cow {
  id: string
  cowCode: string
  name: string
  sensorId?: string
  type?: string
  cattleType?: string
  breed?: string
  gender?: string
  birthDate?: string
  status: string
  healthStatus?: string
}

interface FarmerProfile {
  firstName: string
  lastName: string
  phone: string
  email?: string
  lineId?: string
}

type TabType = 'ข้อมูลฟาร์ม' | 'ข้อมูลโค'

function FarmDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [currentTab, setCurrentTab] = useState<TabType>('ข้อมูลฟาร์ม')
  const [farm, setFarm] = useState<Farm | null>(null)
  const [cows, setCows] = useState<Cow[]>([])
  const [farmerProfile, setFarmerProfile] = useState<FarmerProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCattleType, setSelectedCattleType] = useState('')

  // Fetch heat alerts
  const { data: heatAlertsData } = useGetHeatAlertsQuery()

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    if (id) {
      fetchFarmData()
      fetchFarmerProfile()
    }
  }, [id])

  useEffect(() => {
    if (id && currentTab === 'ข้อมูลโค') {
      fetchCows()
    }
  }, [id, currentTab])

  const fetchFarmData = async () => {
    try {
      const token = storageService.getAuthToken()
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.FARMS.DETAIL(id!)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (!response.ok) throw new Error('Failed to fetch farm data')

      const data = await response.json()
      setFarm(data)
      // Set cows from farm data if available
      if (data.cows) {
        setCows(data.cows)
      }
    } catch (error: any) {
      console.error('Error fetching farm:', error)
      toast.error('ไม่สามารถโหลดข้อมูลฟาร์มได้')
    } finally {
      setLoading(false)
    }
  }

  const fetchFarmerProfile = async () => {
    try {
      const token = storageService.getAuthToken()
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.FARMERS.PROFILE}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.ok) {
        const data = await response.json()
        setFarmerProfile(data)
      }
    } catch (error) {
      console.error('Error fetching farmer profile:', error)
    }
  }

  const fetchCows = async () => {
    try {
      const token = storageService.getAuthToken()
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.FARMS.COWS(id!)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.ok) {
        const data = await response.json()
        // Backend now returns pagination format: { data: [...], total, page, limit, totalPages }
        setCows(data.data || [])
      }
    } catch (error) {
      console.error('Error fetching cows:', error)
    }
  }

  const getFullAddress = () => {
    if (!farm) return '-'
    const parts = [
      farm.address,
      farm.subdistrict,
      farm.district,
      farm.province,
      farm.postalCode,
    ].filter(Boolean)
    return parts.length > 0 ? parts.join(' ') : '-'
  }

  const farmerName = farmerProfile
    ? `${farmerProfile.firstName} ${farmerProfile.lastName}`
    : 'ผู้ใช้งาน'

  // Create a map of cow IDs with active heat alerts
  const cowsWithHeatAlert = new Set(
    heatAlertsData?.data
      ?.filter(alert => alert.status === 'ACTIVE')
      ?.map(alert => alert.cowId) || []
  )

  // Filter cows based on search and cattle type
  const filteredCows = cows.filter(cow => {
    const matchesSearch = !searchTerm ||
      cow.cowCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cow.name && cow.name.toLowerCase().includes(searchTerm.toLowerCase()))

    const matchesCattleType = !selectedCattleType ||
      cow.type === selectedCattleType ||
      cow.cattleType === selectedCattleType

    return matchesSearch && matchesCattleType
  })

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'ใช้งาน'
      case 'INACTIVE':
        return 'ไม่ใช้งาน'
      case 'ARCHIVED':
        return 'เก็บถาวร'
      default:
        return status
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-success-light text-success'
      case 'INACTIVE':
        return 'bg-gray-100 text-gray-600'
      case 'ARCHIVED':
        return 'bg-orange-100 text-orange-600'
      default:
        return 'bg-gray-100 text-gray-600'
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800 mx-auto mb-4"></div>
            <p className="text-text-secondary">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </MainLayout>
    )
  }

  if (!farm) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-text-secondary mb-4">ไม่พบข้อมูลฟาร์ม</p>
            <Button onClick={() => navigate('/farms')}>
              กลับไปหน้ารายการฟาร์ม
            </Button>
          </div>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout title="ข้อมูลฟาร์ม" subtitle={`กำลังดู: ${farm.name}`}>
      <div>
          {/* Back Button & Edit Button */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <button
              onClick={() => navigate('/farms')}
              className="flex items-center gap-2 text-text-secondary hover:text-primary-800 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">กลับไปรายการฟาร์ม</span>
            </button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(`/farms/${id}/edit`)}
              className="flex items-center gap-2 w-full sm:w-auto"
            >
              <Edit className="w-4 h-4" />
              แก้ไขข้อมูล
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-border-medium mb-6 overflow-x-auto">
            <button
              className={`pb-3 px-4 lg:px-6 text-xs lg:text-sm font-medium transition-colors relative whitespace-nowrap ${
                currentTab === 'ข้อมูลฟาร์ม'
                  ? 'text-primary-800'
                  : 'text-text-tertiary hover:text-text-secondary'
              }`}
              onClick={() => setCurrentTab('ข้อมูลฟาร์ม')}
            >
              ข้อมูลฟาร์ม
              {currentTab === 'ข้อมูลฟาร์ม' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-800" />
              )}
            </button>
            <button
              className={`pb-3 px-4 lg:px-6 text-xs lg:text-sm font-medium transition-colors relative whitespace-nowrap ${
                currentTab === 'ข้อมูลโค'
                  ? 'text-primary-800'
                  : 'text-text-tertiary hover:text-text-secondary'
              }`}
              onClick={() => setCurrentTab('ข้อมูลโค')}
            >
              ข้อมูลโค
              {currentTab === 'ข้อมูลโค' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-800" />
              )}
            </button>
          </div>

          {/* Tab Content: ข้อมูลฟาร์ม */}
          {currentTab === 'ข้อมูลฟาร์ม' && (
            <div className="bg-white rounded-lg p-4 lg:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* ชื่อฟาร์ม */}
                  <div className="flex items-center">
                    <span className="text-sm text-text-primary w-32 font-medium">ชื่อฟาร์ม  </span>
                    <span className="text-sm text-text-secondary">:  </span>
                    <span className="flex-1 text-sm text-text-primary">{farm.name || '-'}</span>
                  </div>

                  {/* รหัสฟาร์ม */}
                  <div className="flex items-center">
                    <span className="text-sm text-text-primary w-32 font-medium">รหัสฟาร์ม  </span>
                    <span className="text-sm text-text-secondary">:  </span>
                    <span className="flex-1 text-sm text-text-primary">{farm.farmCode || '-'}</span>
                  </div>

                  {/* ที่อยู่ */}
                  <div className="flex items-start">
                    <span className="text-sm text-text-primary w-32 font-medium pt-1">ที่อยู่  </span>
                    <span className="text-sm text-text-secondary pt-1">:  </span>
                    <span className="flex-1 text-sm text-text-primary">{getFullAddress()}</span>
                  </div>

                  {/* พื้นที่ */}
                  <div className="flex items-center">
                    <span className="text-sm text-text-primary w-32 font-medium">พื้นที่  </span>
                    <span className="text-sm text-text-secondary">:  </span>
                    <span className="flex-1 text-sm text-text-primary">
                      {farm.landArea ? `${farm.landArea} ไร่` : '-'}
                    </span>
                  </div>

                  {/* รูปภาพ */}
                  {farm.images && farm.images.length > 0 && (
                    <div className="flex items-start">
                      <span className="text-sm text-text-primary w-32 font-medium pt-2">รูปภาพ  </span>
                      <span className="text-sm text-text-secondary pt-2">:  </span>
                      <div className="flex-1">
                        <div className="w-full h-48 rounded-lg overflow-hidden mb-3 border border-border-medium">
                          <img
                            src={farm.images[0]}
                            alt="Farm"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* พิกัด */}
                  {(farm.latitude || farm.longitude) && (
                    <div className="flex items-start">
                      <span className="text-sm text-text-primary w-32 font-medium pt-2">พิกัด  </span>
                      <span className="text-sm text-text-secondary pt-2">:  </span>
                      <div className="flex-1 grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-text-secondary mb-1">ละติจูด</label>
                          <div className="text-sm text-text-primary">
                            {farm.latitude || '-'}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-text-secondary mb-1">ลองจิจูด</label>
                          <div className="text-sm text-text-primary">
                            {farm.longitude || '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Map */}
                  {(farm.latitude && farm.longitude) && (
                    <div className="flex items-start">
                      <span className="text-sm text-text-primary w-32 pt-2">แผนที่  </span>
                      <span className="text-sm text-text-secondary pt-2">:  </span>
                      <div className="flex-1">
                        <MapView
                          latitude={farm.latitude}
                          longitude={farm.longitude}
                          name={farm.name}
                          className="h-64"
                        />
                      </div>
                    </div>
                  )}

                  {/* สถานะฟาร์ม */}
                  <div className="flex items-center">
                    <span className="text-sm text-text-primary w-32 font-medium">สถานะฟาร์ม  </span>
                    <span className="text-sm text-text-secondary">:  </span>
                    <div className="flex-1">
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-sm ${getStatusColor(farm.status)}`}
                      >
                        {getStatusLabel(farm.status)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* เจ้าของฟาร์ม */}
                  <div className="flex items-center">
                    <span className="text-sm text-text-primary w-40 font-medium">เจ้าของฟาร์ม  </span>
                    <span className="text-sm text-text-secondary">:  </span>
                    <div className="flex-1 flex items-center gap-2">
                      <User className="w-4 h-4 text-primary-800" />
                      <span className="text-sm text-text-primary">{farmerName}</span>
                    </div>
                  </div>

                  {/* อีเมล */}
                  {farmerProfile?.email && (
                    <div className="flex items-center">
                      <span className="text-sm text-text-primary w-40 font-medium">อีเมล  </span>
                      <span className="text-sm text-text-secondary">:  </span>
                      <span className="flex-1 text-sm text-text-primary">{farmerProfile.email}</span>
                    </div>
                  )}

                  {/* เบอร์โทรศัพท์ */}
                  {farmerProfile?.phone && (
                    <div className="flex items-center">
                      <span className="text-sm text-text-primary w-40 font-medium">เบอร์โทรศัพท์  </span>
                      <span className="text-sm text-text-secondary">:  </span>
                      <span className="flex-1 text-sm text-text-primary">{farmerProfile.phone}</span>
                    </div>
                  )}

                  {/* Line ID */}
                  {farmerProfile?.lineId && (
                    <div className="flex items-center">
                      <span className="text-sm text-text-primary w-40 font-medium">Line ID  </span>
                      <span className="text-sm text-text-secondary">:  </span>
                      <span className="flex-1 text-sm text-text-primary">{farmerProfile.lineId}</span>
                    </div>
                  )}

                  {/* วันที่สร้าง */}
                  <div className="flex items-center">
                    <span className="text-sm text-text-primary w-40 font-medium">วันที่สร้าง  </span>
                    <span className="text-sm text-text-secondary">:  </span>
                    <span className="flex-1 text-sm text-text-primary">
                      {new Date(farm.createdAt).toLocaleDateString('th-TH')}
                    </span>
                  </div>

                  {/* วันที่อัพเดท */}
                  <div className="flex items-center">
                    <span className="text-sm text-text-primary w-40 font-medium">อัพเดทล่าสุด  </span>
                    <span className="text-sm text-text-secondary">:  </span>
                    <span className="flex-1 text-sm text-text-primary">
                      {new Date(farm.updatedAt).toLocaleDateString('th-TH')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab Content: ข้อมูลโค */}
          {currentTab === 'ข้อมูลโค' && (
            <div className="space-y-6">
              {/* Header with Add Button */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-xl font-semibold text-text-primary">โคในฟาร์ม</h3>
                  <p className="text-text-secondary mt-1">
                    ทั้งหมด {filteredCows.length} ตัว
                  </p>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  className="flex items-center gap-2 w-full sm:w-auto"
                  onClick={() => navigate(`/cows/new?farmId=${id}`)}
                >
                  เพิ่มโคใหม่ +
                </Button>
              </div>

              {/* Filters */}
              <div className="bg-white rounded-lg p-6 shadow-sm border border-border-light">
                <div className="space-y-4">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
                    <input
                      type="text"
                      placeholder="ค้นหารหัสโค, ชื่อ..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="w-full h-input pl-10 pr-4 border border-border-medium rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary-800"
                    />
                  </div>

                  {/* Cattle Type Filter - Clickable Buttons */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-3">
                      กลุ่มวัว
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setSelectedCattleType('')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedCattleType === ''
                            ? 'bg-primary-800 text-white'
                            : 'bg-background-tertiary text-text-secondary hover:bg-background-gray'
                        }`}
                      >
                        ทั้งหมด
                      </button>
                      {Object.entries(CATTLE_TYPES).map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => setSelectedCattleType(key)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            selectedCattleType === key
                              ? 'bg-primary-800 text-white'
                              : 'bg-background-tertiary text-text-secondary hover:bg-background-gray'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Cow List Section */}
              {cows.length === 0 ? (
                <div className="bg-background-secondary rounded-lg p-12 text-center">
                  <p className="text-text-tertiary text-lg mb-4">ยังไม่มีโคในฟาร์มนี้</p>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(`/cows/new?farmId=${id}`)}
                  >
                    เพิ่มโคตัวแรก
                  </Button>
                </div>
              ) : filteredCows.length === 0 ? (
                <div className="bg-background-secondary rounded-lg p-12 text-center">
                  <p className="text-text-tertiary text-lg">ไม่พบข้อมูลโคที่ค้นหา</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-border-light overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-background-tertiary">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">ลำดับ</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">รหัส</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">ชื่อโค</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">กลุ่มวัว</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">เซนเซอร์</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">สถานะ</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">อายุ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCows.map((cow, index) => {
                          // Calculate age in months
                          const { totalMonths: ageInMonths } = cow.birthDate
                            ? calculateAge(cow.birthDate)
                            : { totalMonths: 0 }

                          return (
                            <tr
                              key={cow.id}
                              className="border-t border-border-light hover:bg-background-tertiary cursor-pointer transition-colors"
                              onClick={() => navigate(`/cows/${cow.id}`)}
                            >
                              <td className="px-4 py-3 text-sm text-text-primary">{index + 1}</td>
                              <td className="px-4 py-3 text-sm text-text-primary">{cow.cowCode}</td>
                              <td className="px-4 py-3 text-sm text-text-primary font-medium">
                                <div className="flex items-center gap-2">
                                  <span>{cow.name || '-'}</span>
                                  {cowsWithHeatAlert.has(cow.id) && (
                                    <span
                                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-error-light text-error text-xs font-semibold"
                                      title="โคเคลื่อนไหวมากกว่าปกติ"
                                    >
                                      <AlertTriangle className="w-3 h-3" />
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {(cow.cattleType || cow.type) ? (
                                  <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-primary-100 text-primary-800">
                                    {CATTLE_TYPES[(cow.cattleType || cow.type) as keyof typeof CATTLE_TYPES]}
                                  </span>
                                ) : (
                                  <span className="text-text-tertiary">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-text-primary">{cow.sensorId || '-'}</td>
                              <td className="px-4 py-3 text-sm">
                                <span
                                  className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                                    (cow.healthStatus || cow.status) === 'HEALTHY'
                                      ? 'bg-success-light text-success'
                                      : (cow.healthStatus || cow.status) === 'SICK'
                                      ? 'bg-error-light text-error'
                                      : (cow.healthStatus || cow.status) === 'TREATMENT'
                                      ? 'bg-warning-light text-warning'
                                      : 'bg-info-light text-info'
                                  }`}
                                >
                                  {(cow.healthStatus || cow.status) === 'HEALTHY'
                                    ? 'สุขภาพดี'
                                    : (cow.healthStatus || cow.status) === 'SICK'
                                    ? 'ป่วย'
                                    : (cow.healthStatus || cow.status) === 'TREATMENT'
                                    ? 'รักษา'
                                    : 'หายแล้ว'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-text-primary">
                                {ageInMonths > 0 ? `${ageInMonths} เดือน` : '-'}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
    </MainLayout>
  )
}

export default FarmDetailPage
